import { EMPTY_TILE } from "../document/packed-tile.ts";
import type { MapDocument, TerrainLayer, TilesetRef, WorldDocument } from "../document/types.ts";
import { FACE_HEIGHT_MAX, compileFace, extendFaces, faceRows, validateTerrainFaces } from "../terrain/faces.ts";
import { applyRuleStage, compileRuleStage, type CompiledStage } from "../terrain/match.ts";
import { missingTerrainMessage } from "../terrain/missing.ts";
import { buildCornerIndex, resolveCornerLayer } from "../terrain/resolve.ts";
import type { Rule, RuleSet, RuleStage } from "../terrain/rules.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import { buildCollisionGrid } from "./collision.ts";
import type { CompileContext, CompileWarning } from "./context.ts";

/**
 * Resolves every tile-bearing layer of a map to packed cells over the whole map (ADR-016 items 3 and 4):
 * terrain layers through the corner resolver, their bound rule set and, for a layer setting a face height,
 * the face pass (ADR-072); tile layers as they are. Rules read neighbours across chunk edges, so this runs
 * before slicing.
 */

/**
 * One resolved layer in document order.
 */
export interface ResolvedTileLayer {
  readonly id: string;
  readonly name: string;
  readonly variant: string | null;
  readonly tiles: Uint32Array;
}

/**
 * A resolved map: tile-bearing layers, the collision grid (`width * height` bytes, ADR-017) and warnings.
 */
export interface ResolvedMap {
  readonly layers: readonly ResolvedTileLayer[];
  readonly collision: Uint8Array;
  readonly warnings: readonly CompileWarning[];
}

/**
 * Compiled rule sets, keyed by id, built once per `resolveMap` call.
 */
type StageCache = Map<string, readonly CompiledStage[]>;

/**
 * Reports whether every tile a rule references belongs to a tileset the world lists. A rule that fails this
 * can never match a world cell (its input names a tile no cell can hold) or can never be applied (its output
 * does), so the compiler drops it rather than rejecting the world (ADR-016 item 9).
 *
 * @param rule The rule.
 * @param tilesetIds The world's tileset ids.
 * @returns True when the rule is representable in this world.
 */
function representable(rule: Rule, tilesetIds: ReadonlySet<string>): boolean {
  const layers = [...rule.inputs, ...rule.outputs];
  return layers.every((layer) => layer.cells.every(({ cell }) => cell.kind !== "tile" || tilesetIds.has(cell.tile.tileset)));
}

/**
 * Drops the rules of a stage that this world cannot represent.
 *
 * @param stage The stage.
 * @param tilesetIds The world's tileset ids.
 * @returns The stage with only representable rules.
 */
function representableStage(stage: RuleStage, tilesetIds: ReadonlySet<string>): RuleStage {
  return { ...stage, rules: stage.rules.filter((rule) => representable(rule, tilesetIds)) };
}

/**
 * Finds a rule set's compiled stages, compiling on first use.
 *
 * @param id Rule set id.
 * @param world World, for the tileset table.
 * @param context Compile context.
 * @param cache Per-call cache.
 * @returns The compiled stages.
 * @throws Error when the rule set is missing.
 */
function compiledStages(id: string, world: WorldDocument, context: CompileContext, cache: StageCache): readonly CompiledStage[] {
  const cached = cache.get(id);
  if (cached !== undefined) {
    return cached;
  }
  const ruleSet = context.ruleSets.find((r) => r.id === id);
  if (ruleSet === undefined) {
    throw new Error(`rule set ${id} is not in the compile context`);
  }
  const stages = compileRuleSetForWorld(ruleSet, world.tilesets);
  cache.set(id, stages);
  return stages;
}

/**
 * Compiles a rule set against a world's tilesets, dropping rules the world cannot represent (ADR-016 item
 * 9) while keeping every stage in place so stage indices are stable. The compiler and the editor's
 * incremental sessions both use this, so they run identical stages.
 *
 * @param ruleSet The rule set.
 * @param tilesets The world's tilesets.
 * @returns One compiled stage per rule set stage.
 */
export function compileRuleSetForWorld(ruleSet: RuleSet, tilesets: readonly TilesetRef[]): CompiledStage[] {
  const tilesetIds = new Set(tilesets.map((t) => t.id));
  return ruleSet.stages.map((stage) => compileRuleStage(representableStage(stage, tilesetIds), tilesets));
}

/**
 * Checks a terrain layer's face height against its terrain: a terrain that declares no face takes none,
 * and a height lies from the face's own rows to `FACE_HEIGHT_MAX`.
 *
 * @param layer The terrain layer.
 * @param terrain Its terrain.
 * @throws Error naming the layer when the height cannot be drawn.
 */
function checkFaceHeight(layer: TerrainLayer, terrain: TerrainDefinition): void {
  const height = layer.faceHeight;
  if (height === undefined) {
    return;
  }
  if (terrain.face === undefined) {
    throw new Error(`layer ${layer.id} sets a face height of ${height}, but terrain ${terrain.id} declares no face`);
  }
  const rows = faceRows(terrain.face);
  if (!Number.isInteger(height) || height < rows || height > FACE_HEIGHT_MAX) {
    throw new Error(`layer ${layer.id} sets face height ${height}, outside the ${rows} rows of terrain ${terrain.id}'s face to ${FACE_HEIGHT_MAX}`);
  }
}

/**
 * Resolves one terrain layer: corners to tiles, then the bound rule set, then the face pass when the layer
 * sets a face height other than its terrain's face's own. The pass's hash word is the rule set's number of
 * stages, so its draws never coincide with a rule's.
 *
 * @param world World document.
 * @param map Map document.
 * @param layer The terrain layer.
 * @param layerIndex Its position in `map.layers`.
 * @param context Compile context.
 * @param cache Compiled stage cache.
 * @returns The packed cells and any unresolved cells.
 * @throws Error with `missingTerrainMessage` when the terrain is missing, or when its tileset or its rule set is, or the layer's face height cannot
 *   be drawn.
 */
function resolveTerrainLayer(world: WorldDocument, map: MapDocument, layer: TerrainLayer, layerIndex: number, context: CompileContext, cache: StageCache): { tiles: Uint32Array; warnings: CompileWarning[] } {
  const terrain = context.terrains.find((t) => t.id === layer.terrain);
  if (terrain === undefined) {
    throw new Error(`map ${map.name}, layer ${layer.name}: ${missingTerrainMessage(layer.terrain)} (terrain ${layer.terrain})`);
  }
  const tilesetIndex = world.tilesets.findIndex((t) => t.id === terrain.tileset);
  if (tilesetIndex < 0) {
    throw new Error(`terrain ${terrain.id} uses tileset ${terrain.tileset}, which the world does not list`);
  }
  checkFaceHeight(layer, terrain);
  const resolved = resolveCornerLayer({ terrain, tilesetIndex, width: map.width, height: map.height, corners: layer.corners, seed: map.seed, layerIndex, index: buildCornerIndex(terrain) });
  const warnings = resolved.warnings.map((w) => ({ mapId: map.id, layerId: layer.id, x: w.x, y: w.y, corners: w.corners }));
  if (terrain.ruleSet === undefined) {
    return { tiles: resolved.tiles, warnings };
  }
  const stages = compiledStages(terrain.ruleSet, world, context, cache);
  const layers = new Map<string, Uint32Array>([[terrain.ruleSet, resolved.tiles]]);
  stages.forEach((stage, stageIndex) => {
    applyRuleStage(stage, { width: map.width, height: map.height, layers, seed: map.seed, layerIndex, stageIndex });
  });
  const tiles = layers.get(terrain.ruleSet) as Uint32Array;
  if (layer.faceHeight === undefined || terrain.face === undefined || layer.faceHeight === faceRows(terrain.face)) {
    return { tiles, warnings };
  }
  const output = new Uint32Array(tiles.length);
  extendFaces({ face: compileFace(terrain.face, tilesetIndex), source: tiles, output, width: map.width, height: map.height, faceHeight: layer.faceHeight, seed: map.seed, layerIndex, stageIndex: stages.length });
  return { tiles: output, warnings };
}

/**
 * Resolves a map's terrain and tile layers and nothing else: the collision grid is left unbuilt.
 *
 * @param world World document.
 * @param map One of its maps.
 * @param context Compile context.
 * @returns Resolved layers in document order (prop and entity layers are not included) and warnings.
 * @throws Error when a terrain, tileset or rule set the map needs is missing, a terrain of the context declares a
 *   malformed face (naming the terrain) or a layer's face height cannot be drawn (naming the layer).
 */
export function resolveTileLayers(world: WorldDocument, map: MapDocument, context: CompileContext): { readonly layers: readonly ResolvedTileLayer[]; readonly warnings: readonly CompileWarning[] } {
  const faceIssues = validateTerrainFaces(context.terrains);
  if (faceIssues.length > 0) {
    throw new Error(faceIssues.map((issue) => `terrain ${issue.terrain} declares a malformed face: ${issue.reason}`).join("; "));
  }
  const cache: StageCache = new Map();
  const layers: ResolvedTileLayer[] = [];
  const warnings: CompileWarning[] = [];
  map.layers.forEach((layer, layerIndex) => {
    if (layer.kind === "terrain") {
      const result = resolveTerrainLayer(world, map, layer, layerIndex, context, cache);
      layers.push({ id: layer.id, name: layer.name, variant: layer.variant, tiles: result.tiles });
      warnings.push(...result.warnings);
    } else if (layer.kind === "tile") {
      const tiles = new Uint32Array(map.width * map.height).fill(EMPTY_TILE);
      tiles.set(layer.tiles);
      layers.push({ id: layer.id, name: layer.name, variant: null, tiles });
    }
  });
  return { layers, warnings };
}

/**
 * Resolves a map's terrain and tile layers.
 *
 * @param world World document.
 * @param map One of its maps.
 * @param context Compile context.
 * @returns Resolved layers in document order (prop and entity layers are not included), the collision grid
 *   and warnings.
 * @throws Error when a terrain, tileset or rule set the map needs is missing, a collision rule is invalid,
 *   a terrain of the context declares a malformed face (naming the terrain) or a layer's face height cannot
 *   be drawn (naming the layer).
 */
export function resolveMap(world: WorldDocument, map: MapDocument, context: CompileContext): ResolvedMap {
  const { layers, warnings } = resolveTileLayers(world, map, context);
  const collision = buildCollisionGrid(world, map, { layers }, context, { objects: false });
  return { layers, collision, warnings };
}
